import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface Props {
  settings: Record<string, unknown>;
  updateSetting: (key: string, value: unknown) => void;
}

export default function SettingsLeagues({ settings, updateSetting }: Props) {
  const { t } = useTranslation("admin");
  const defaults = (settings.league_defaults as Record<string, unknown>) || {};

  const update = (key: string, value: unknown) => {
    updateSetting("league_defaults", { ...defaults, [key]: value });
  };

  const get = <T,>(key: string, fallback: T): T => {
    return (defaults[key] as T) ?? fallback;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("settings.leagues.title")}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t("settings.leagues.description")}
        </p>
      </div>

      {/* 1. Matchformat */}
      <CollapsibleSection title={t("settings.leagues.sectionMatchFormat")} defaultOpen>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">{t("settings.leagues.fieldMatchDuration")}</Label>
            <Select
              value={String(get("match_duration_minutes", 90))}
              onValueChange={(v) => update("match_duration_minutes", Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="60">60</SelectItem>
                <SelectItem value="75">75</SelectItem>
                <SelectItem value="90">90</SelectItem>
                <SelectItem value="105">105</SelectItem>
                <SelectItem value="120">120</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("settings.leagues.fieldSets")}</Label>
            <Select
              value={String(get("sets", 3))}
              onValueChange={(v) => update("sets", Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="3">3</SelectItem>
                <SelectItem value="5">5</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("settings.leagues.fieldTiebreakAt")}</Label>
            <Select
              value={get("tiebreak_at", "6-6")}
              onValueChange={(v) => update("tiebreak_at", v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="6-6">6-6</SelectItem>
                <SelectItem value="5-5">5-5</SelectItem>
                <SelectItem value="4-4">4-4</SelectItem>
                <SelectItem value="none">{t("settings.leagues.noTiebreak")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("settings.leagues.fieldPointType")}</Label>
            <Select
              value={get("point_type", "golden_point")}
              onValueChange={(v) => update("point_type", v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="golden_point">Golden point</SelectItem>
                <SelectItem value="advantage">Advantage</SelectItem>
                <SelectItem value="starpoint">Starpoint</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-6 pt-2">
          <div className="flex items-center gap-2">
            <Switch
              checked={get("play_all_sets", true)}
              onCheckedChange={(v) => update("play_all_sets", v)}
            />
            <Label className="text-sm">{t("settings.leagues.playAllSets")}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={get("time_limit_applies", true)}
              onCheckedChange={(v) => update("time_limit_applies", v)}
            />
            <div>
              <Label className="text-sm">{t("settings.leagues.timeLimit")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.leagues.timeLimitDesc")}
              </p>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* 2. Poängsystem */}
      <CollapsibleSection title={t("settings.leagues.sectionPointSystem")}>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldSetWin")}</Label>
            <NumberInput
              value={get("set_win", 3)}
              onValueChange={(v) => update("set_win", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldSetLoss")}</Label>
            <NumberInput
              value={get("set_loss", 0)}
              onValueChange={(v) => update("set_loss", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldSetDraw")}</Label>
            <NumberInput
              value={get("set_draw", 1)}
              onValueChange={(v) => update("set_draw", v)}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldWoWinner")}</Label>
            <NumberInput
              value={get("walkover_winner", 9)}
              onValueChange={(v) => update("walkover_winner", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldWoLoser")}</Label>
            <NumberInput
              value={get("walkover_loser", 0)}
              onValueChange={(v) => update("walkover_loser", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldWoNoNotice")}</Label>
            <NumberInput
              value={get("walkover_no_notice_penalty", -2)}
              onValueChange={(v) => update("walkover_no_notice_penalty", v)}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("settings.leagues.fieldMaxWoBeforeBlock")}</Label>
          <NumberInput
            className="w-32"
            value={get("max_wo_before_block", 2)}
            onValueChange={(v) => update("max_wo_before_block", v)}
          />
          <p className="text-xs text-muted-foreground">
            {t("settings.leagues.maxWoHint")}
          </p>
        </div>
        {get("time_limit_applies", true) && (
          <div className="border-t pt-3 space-y-3">
            <p className="text-xs font-medium text-muted-foreground">
              {t("settings.leagues.timeLimitPoints")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t("settings.leagues.fieldUnfinishedSetWin")}</Label>
                <NumberInput
                  value={get("unfinished_set_win", 2)}
                  onValueChange={(v) => update("unfinished_set_win", v)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("settings.leagues.fieldUnfinishedSetLoss")}</Label>
                <NumberInput
                  value={get("unfinished_set_loss", 0)}
                  onValueChange={(v) => update("unfinished_set_loss", v)}
                />
              </div>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* 3. Divisioner & Lag */}
      <CollapsibleSection title={t("settings.leagues.sectionDivisionsTeams")}>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldMaxTeamsPerDivision")}</Label>
            <NumberInput
              value={get("max_teams_per_division", 8)}
              onValueChange={(v) => update("max_teams_per_division", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldPromotion")}</Label>
            <NumberInput
              value={get("promotion_count", 1)}
              onValueChange={(v) => update("promotion_count", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldRelegation")}</Label>
            <NumberInput
              value={get("relegation_count", 1)}
              onValueChange={(v) => update("relegation_count", v)}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* 4. Ombokning */}
      <CollapsibleSection title={t("settings.leagues.sectionReschedule")}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldRescheduleDeadline")}</Label>
            <NumberInput
              value={get("reschedule_deadline_hours", 48)}
              onValueChange={(v) => update("reschedule_deadline_hours", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldLateRescheduleFee")}</Label>
            <NumberInput
              value={get("late_reschedule_fee", 0)}
              onValueChange={(v) => update("late_reschedule_fee", v)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs">{t("settings.leagues.fieldFeeType")}</Label>
            <Select
              value={get("late_reschedule_fee_type", "fixed")}
              onValueChange={(v) => update("late_reschedule_fee_type", v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">{t("settings.leagues.feeFixed")}</SelectItem>
                <SelectItem value="percent">{t("settings.leagues.feePercent")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldAutoRelease")}</Label>
            <NumberInput
              value={get("auto_release_hours", 24)}
              onValueChange={(v) => update("auto_release_hours", v)}
            />
            <p className="text-xs text-muted-foreground">
              {t("settings.leagues.autoReleaseHint")}
            </p>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("settings.leagues.fieldAvailabilityDeadline")}</Label>
          <NumberInput
            className="w-32"
            value={get("availability_deadline_hours", 48)}
            onValueChange={(v) => update("availability_deadline_hours", v)}
          />
          <p className="text-xs text-muted-foreground">
            {t("settings.leagues.availabilityDeadlineHint")}
          </p>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label className="text-sm">{t("settings.leagues.autoUnavailable")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("settings.leagues.autoUnavailableDesc")}
            </p>
          </div>
          <Switch
            checked={get("auto_unavailable_on_no_response", false)}
            onCheckedChange={(v) => update("auto_unavailable_on_no_response", v)}
          />
        </div>
      </CollapsibleSection>

      {/* 5. Synlighet */}
      <CollapsibleSection title={t("settings.leagues.sectionVisibility")}>
        <div className="space-y-1">
          <Label className="text-xs">{t("settings.leagues.fieldRoundsVisible")}</Label>
          <NumberInput
            className="w-32"
            placeholder="Alla"
            value={get<number | undefined>("rounds_visible_ahead", undefined)}
            onValueChange={(v) => update("rounds_visible_ahead", v || null)}
          />
          <p className="text-xs text-muted-foreground">
            {t("settings.leagues.roundsVisibleHint")}
          </p>
        </div>
      </CollapsibleSection>

      {/* 6. Ekonomi */}
      <CollapsibleSection title={t("settings.leagues.sectionEconomy")}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldCostPerPlayer")}</Label>
            <NumberInput
              value={get("cost_per_player", 0)}
              onValueChange={(v) => update("cost_per_player", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldCourtCost")}</Label>
            <NumberInput
              value={get("court_cost_per_match", 0)}
              onValueChange={(v) => update("court_cost_per_match", v)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldAdminFee")}</Label>
            <NumberInput
              value={get("admin_fee_per_match", 0)}
              onValueChange={(v) => update("admin_fee_per_match", v)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldVat")}</Label>
            <Select
              value={String(get("vat_rate", 0.25))}
              onValueChange={(v) => update("vat_rate", Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0%</SelectItem>
                <SelectItem value="0.06">6%</SelectItem>
                <SelectItem value="0.12">12%</SelectItem>
                <SelectItem value="0.25">25%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label className="text-sm">{t("settings.leagues.freeForMembers")}</Label>
            <p className="text-xs text-muted-foreground">{t("settings.leagues.freeForMembersDesc")}</p>
          </div>
          <Switch
            checked={get("members_play_free", false)}
            onCheckedChange={(v) => update("members_play_free", v)}
          />
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label className="text-sm">{t("settings.leagues.perRoundPayment")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("settings.leagues.perRoundPaymentDesc")}
            </p>
          </div>
          <Switch
            checked={get("per_round_payment_enabled", false)}
            onCheckedChange={(v) => update("per_round_payment_enabled", v)}
          />
        </div>
        {get("per_round_payment_enabled", false) && (
          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("settings.leagues.fieldPerRoundPrice")}</Label>
              <NumberInput
                value={get("per_round_price", 0)}
                onValueChange={(v) => update("per_round_price", v)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("settings.leagues.fieldPerRoundMemberPrice")}</Label>
              <NumberInput
                value={get("per_round_price_member", 0)}
                onValueChange={(v) => update("per_round_price_member", v)}
              />
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* 7. Regler */}
      <CollapsibleSection title={t("settings.leagues.sectionRules")}>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldRulesSv")}</Label>
            <Textarea
              rows={5}
              value={get("rules_text", "")}
              onChange={(e) => update("rules_text", e.target.value)}
              placeholder={t("settings.leagues.placeholderRulesSv")}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("settings.leagues.fieldRulesEn")}</Label>
            <Textarea
              rows={5}
              value={get("rules_text_en", "")}
              onChange={(e) => update("rules_text_en", e.target.value)}
              placeholder="League rules shown to players..."
            />
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border p-3 text-sm font-medium hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
        {title}
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3 space-y-3 px-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
